import { isPasswordRecoveryPath } from '../../_helpers/password-recovery';
import { isDemoEntryPath } from '../../_helpers/demo-entry';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { Subscription, filter } from 'rxjs';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { TopbarComponent } from '../topbar/topbar.component';
import { SessionService } from '../../_services/session/session.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  standalone: true,
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  imports: [CommonModule, RouterModule, SidebarComponent, TopbarComponent],
})
export class LayoutComponent implements OnInit, OnDestroy {
  public readonly currentYear = new Date().getFullYear();
  public isSidebarOpen = false;
  public isAuthenticated = false;
  public currentPageTitle = '';
  public currentPageIcon = '';

  private subscriptions: Subscription[] = [];

  // Page title mappings
  private pageTitles: Record<string, { title: string; icon: string }> = {
    dashboard: { title: 'MENU.DASHBOARD', icon: 'fas fa-th-large' },
    'ai-assistant': { title: 'MENU.AI_ASSISTANT', icon: 'fas fa-robot' },
    incidents: { title: 'MENU.INCIDENTS', icon: 'fas fa-triangle-exclamation' },
    appointments: { title: 'MENU.APPOINTMENTS', icon: 'fas fa-calendar-alt' },
    patients: { title: 'MENU.PATIENTS', icon: 'fas fa-users' },
    sales: { title: 'MENU.SALES', icon: 'fas fa-cash-register' },
    statistics: { title: 'MENU.STATS', icon: 'fas fa-chart-line' },
    analytics: { title: 'MENU.STATS', icon: 'fas fa-chart-line' },
    administration: { title: 'MENU.ADMIN', icon: 'fas fa-cogs' },
    'online-booking': {
      title: 'ONLINE_BOOKING_SHARE.TITLE',
      icon: 'fas fa-calendar-check',
    },
    profile: { title: 'MENU.PROFILE', icon: 'fas fa-user' },
    tasks: { title: 'MENU.TASKS', icon: 'fas fa-tasks' },
    support: { title: 'SUPPORT.TITLE', icon: 'fas fa-headset' },
  };

  private statisticsSubsectionTitles: Record<string, string> = {
    dashboard: 'STATISTICS.NAV.DASHBOARD',
    revenue: 'STATISTICS.NAV.REVENUE',
    treatments: 'STATISTICS.NAV.TREATMENTS',
    patients: 'STATISTICS.NAV.PATIENTS',
    appointments: 'STATISTICS.NAV.APPOINTMENTS',
  };

  private patientSubsectionTitles: Record<string, string> = {
    home: 'PATIENT_SPACE.NAV.HOME',
    discover: 'PATIENT_SPACE.NAV.DISCOVER',
    appointments: 'PATIENT_SPACE.NAV.APPOINTMENTS',
    clinics: 'PATIENT_SPACE.NAV.CLINICS',
    purchases: 'PATIENT_SPACE.NAV.PURCHASES',
    profile: 'PATIENT_SPACE.NAV.PROFILE',
  };

  constructor(
    private session: SessionService,
    private router: Router,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    // Check initial authentication state (from localStorage)
    const currentUser = this.session.getUser();
    this.isAuthenticated = !!currentUser && !isPasswordRecoveryPath(this.router.url) && !isDemoEntryPath(this.router.url);

    // Subscribe to authentication state changes
    const userSub = this.session.getUser$().subscribe((user) => {
      this.isAuthenticated = !!user && !isPasswordRecoveryPath(this.router.url) && !isDemoEntryPath(this.router.url);
      if (!user) {
        this.isSidebarOpen = false;
      }
    });
    this.subscriptions.push(userSub);

    // Subscribe to route changes to update page title and auth state
    const routerSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // Re-check auth on each navigation (in case of page refresh)
        const user = this.session.getUser();
        this.isAuthenticated = !!user && !isPasswordRecoveryPath(this.router.url) && !isDemoEntryPath(this.router.url);

        this.updatePageTitle(event.urlAfterRedirects);
        // Close sidebar on navigation (mobile UX)
        if (window.innerWidth < 992) {
          this.isSidebarOpen = false;
        }
      });
    this.subscriptions.push(routerSub);

    // Initial page title
    this.updatePageTitle(this.router.url);

    // Update title on language change
    const langSub = this.translateService.onLangChange.subscribe(() => {
      this.updatePageTitle(this.router.url);
    });
    this.subscriptions.push(langSub);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  toggleSidebar() {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  closeSidebar() {
    this.isSidebarOpen = false;
  }

  private updatePageTitle(url: string) {
    const cleanUrl = url.split('?')[0].split('#')[0];
    const segments = cleanUrl.split('/').filter(Boolean);

    const patientSegmentIndex = segments.indexOf('patient');
    if (patientSegmentIndex !== -1) {
      const sectionSegment = segments[patientSegmentIndex + 1];
      const subsectionKey = sectionSegment
        ? this.patientSubsectionTitles[sectionSegment]
        : undefined;

      const keysToTranslate = subsectionKey
        ? ['PATIENT_SPACE.TITLE', subsectionKey]
        : ['PATIENT_SPACE.TITLE'];

      this.translateService
        .get(keysToTranslate)
        .subscribe((translated: Record<string, string>) => {
          const patientTitle =
            translated['PATIENT_SPACE.TITLE'] ?? 'Espacio del paciente';
          const subsectionTitle = subsectionKey
            ? translated[subsectionKey]
            : undefined;
          this.currentPageTitle = subsectionTitle
            ? `${patientTitle} / ${subsectionTitle}`
            : patientTitle;
          this.currentPageIcon = 'fas fa-user-circle';
        });
      return;
    }

    const statisticsSegmentIndex = segments.indexOf('statistics');
    if (statisticsSegmentIndex !== -1) {
      const sectionSegment = segments[statisticsSegmentIndex + 1];
      const subsectionKey = sectionSegment
        ? this.statisticsSubsectionTitles[sectionSegment]
        : undefined;

      const keysToTranslate = subsectionKey
        ? ['MENU.STATS', subsectionKey]
        : ['MENU.STATS'];

      this.translateService
        .get(keysToTranslate)
        .subscribe((translated: Record<string, string>) => {
          const statsTitle = translated['MENU.STATS'] ?? 'Estadísticas';
          const subsectionTitle = subsectionKey
            ? translated[subsectionKey]
            : undefined;
          this.currentPageTitle = subsectionTitle
            ? `${statsTitle} / ${subsectionTitle}`
            : statsTitle;
          this.currentPageIcon = 'fas fa-chart-line';
        });
      return;
    }

    if (segments.includes('support')) {
      this.translateService.get(segments[0] === 'administration' ? 'SUPPORT.ADMIN_TITLE' : 'SUPPORT.TITLE')
        .subscribe((title) => { this.currentPageTitle = title; this.currentPageIcon = 'fas fa-headset'; });
      return;
    }

    // Find matching page title
    for (const segment of segments) {
      if (this.pageTitles[segment]) {
        const pageInfo = this.pageTitles[segment];
        this.translateService.get(pageInfo.title).subscribe((translated) => {
          this.currentPageTitle = translated;
          this.currentPageIcon = pageInfo.icon;
        });
        return;
      }
    }

    // Default fallback
    this.currentPageTitle = 'Virtualcenter';
    this.currentPageIcon = 'fas fa-home';
  }
}
